import type {UpdateUserFormPayload} from "./update-user.schema";
import type {MutationCallbacks} from "../../types/callbacks";
import type {User} from "@project/sdk/admin";

import {useMutation, useQueryClient} from "@tanstack/react-query";

import {userKeys} from "../../user-keys";

import {updateUserMutationOptions} from "./update-user.mutation-options";

/**
 * Command hook: Update a user with optimistic updates.
 *
 * DEFAULT BEHAVIOR (always applied):
 * - Optimistic update to detail cache
 * - Rollback on error
 * - Invalidate list queries on success
 *
 * USER CALLBACKS (optional, applied after defaults):
 * - onMutate: Custom logic before mutation (e.g., analytics)
 * - onError: Custom error handling (e.g., toast)
 * - onSuccess: Custom success handling (e.g., toast, navigation)
 *
 * @param userId - The ID of the user being updated
 * @param callbacks - Optional callbacks for custom logic
 *
 * @example
 * const mutation = useUpdateUser(userId, {
 *   onSuccess: (user) => showToast(`Updated ${user.name}`),
 *   onError: (err) => showToast(`Error: ${err.message}`),
 * });
 */
export function useUpdateUser(
  userId: string,
  callbacks?: Partial<MutationCallbacks<UpdateUserFormPayload, User>>,
) {
  const queryClient = useQueryClient();
  const detailKey = userKeys.detail(userId);

  return useMutation({
    ...updateUserMutationOptions(userId),

    onMutate: async (payload: UpdateUserFormPayload) => {
      // ─── DEFAULT: Optimistic update ───────────────────────────────
      await queryClient.cancelQueries({queryKey: detailKey});
      const previousUser = queryClient.getQueryData<User>(detailKey);

      queryClient.setQueryData<User>(detailKey, (old) => (old ? {...old, ...payload} : old));

      // ─── USER: Custom onMutate callback ───────────────────────────
      const userContext = await callbacks?.onMutate?.(payload);

      return {previousUser, userContext};
    },

    onError: (_err, _vars, ctx) => {
      // ─── DEFAULT: Rollback optimistic update ──────────────────────
      if (ctx?.previousUser) {
        queryClient.setQueryData(detailKey, ctx.previousUser);
      }

      // ─── USER: Custom onError callback ───────────────────────────
      callbacks?.onError?.(_err, _vars, ctx);
    },

    onSuccess: (updatedUser) => {
      // ─── DEFAULT: Update cache + invalidate lists ─────────────────
      queryClient.setQueryData(detailKey, updatedUser);
      void queryClient.invalidateQueries({queryKey: userKeys.lists()});

      // ─── USER: Custom onSuccess callback ──────────────────────────
      callbacks?.onSuccess?.(updatedUser);
    },
  });
}
